import { Component, ViewEncapsulation, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

const MAX_FILE_SIZE = 3 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

export interface UpdateAvatarDialogData {
  currentAvatar?: string | null;
}

@Component({
  selector: 'app-update-avatar-dialog',
  standalone: true,
  imports: [MatButtonModule, MatDialogModule, MatIconModule, TranslocoModule],
  templateUrl: './update-avatar-dialog.component.html',
  styles: [
    `
      .update-avatar-dialog-panel {
        @screen md {
          @apply w-96;
        }
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class UpdateAvatarDialogComponent {
  private _dialogRef = inject(MatDialogRef<UpdateAvatarDialogComponent>);
  private _translocoService = inject(TranslocoService);
  private _data = inject<UpdateAvatarDialogData>(MAT_DIALOG_DATA, { optional: true });

  selectedFile: File | null = null;
  previewUrl = signal<string | null>(this._data?.currentAvatar ?? null);
  errorMessage = signal<string | null>(null);

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';

    if (!file) {
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      this.errorMessage.set(this._translocoService.translate('user.file-type-not-supported'));
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      this.errorMessage.set(this._translocoService.translate('user.file-too-large'));
      return;
    }

    this.errorMessage.set(null);
    this.selectedFile = file;

    const reader = new FileReader();
    reader.onload = () => {
      this.previewUrl.set(reader.result as string);
    };
    reader.readAsDataURL(file);
  }

  cancel(): void {
    this._dialogRef.close();
  }

  confirm(): void {
    if (!this.selectedFile) {
      this.errorMessage.set(this._translocoService.translate('user.no-file-selected'));
      return;
    }

    this._dialogRef.close(this.selectedFile);
  }
}
